import React, { useState, useEffect } from 'react';
import { useSubmitLock } from '../hooks/useSubmitLock';
import { useNotification } from '../contexts/NotificationContext';
import { rbacService } from '../services/rbacService';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const RoleManagement = () => {
  const [submitting, runSubmit] = useSubmitLock();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true,
    permission_ids: []
  });
  const { showNotification } = useNotification();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesData, permissionsData] = await Promise.all([
        rbacService.getRoles(),
        rbacService.getPermissions()
      ]);
      setRoles(rolesData);
      setPermissions(permissionsData);
    } catch (err) {
      setError(err.message);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    setSelectedRole(role);
    if (role) {
      setFormData({
        name: role.name,
        display_name: role.display_name,
        description: role.description || '',
        is_active: role.is_active,
        permission_ids: role.permissions ? role.permissions.map(p => p.id) : []
      });
    } else {
      setFormData({
        name: '',
        display_name: '',
        description: '',
        is_active: true,
        permission_ids: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRole(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      is_active: true,
      permission_ids: []
    });
  };

  const handleSaveRole = async () => {
    try {
      if (selectedRole) {
        // Atualizar role
        const updateData = {
          display_name: formData.display_name,
          description: formData.description,
          is_active: formData.is_active,
          permission_ids: formData.permission_ids
        };
        await rbacService.updateRole(selectedRole.id, updateData);
        showNotification('Role atualizada com sucesso', 'success');
      } else {
        // Criar role
        await rbacService.createRole(formData);
        showNotification('Role criada com sucesso', 'success');
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Tem certeza que deseja deletar esta role? Esta ação não pode ser desfeita.')) {
      try {
        await rbacService.deleteRole(roleId);
        showNotification('Role deletada com sucesso', 'success');
        loadData();
      } catch (err) {
        showNotification(err.message, 'error');
      }
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }));
  };

  const generateRoleName = (displayName) => {
    return displayName.toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const groupPermissionsByModule = (permissions) => {
    const grouped = {};
    permissions.forEach(permission => {
      if (!grouped[permission.module]) {
        grouped[permission.module] = [];
      }
      grouped[permission.module].push(permission);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const permissionGroups = groupPermissionsByModule(permissions);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Gerenciamento de Roles
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nova Role
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Permissões</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sistema</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {role.display_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {role.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {role.permissions ? role.permissions.length : 0} permissões
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.is_active ? 'Ativo' : 'Inativo'}
                        color={role.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {role.is_system && (
                        <Chip
                          label="Sistema"
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleOpenDialog(role)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      {!role.is_system && (
                        <IconButton
                          onClick={() => handleDeleteRole(role.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Dialog para criar/editar role */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          PaperProps={{ sx: { height: '80vh' } }}
        >
          <DialogTitle>
            {selectedRole ? 'Editar Role' : 'Nova Role'}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Nome de Exibição"
                value={formData.display_name}
                onChange={(e) => {
                  const displayName = e.target.value;
                  setFormData({ 
                    ...formData, 
                    display_name: displayName,
                    name: selectedRole ? formData.name : generateRoleName(displayName)
                  });
                }}
                margin="dense"
                required
              />
              
              <TextField
                fullWidth
                label="Nome Interno"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="dense"
                disabled={!!selectedRole}
                required
                helperText={selectedRole ? "Nome interno não pode ser alterado" : "Gerado automaticamente do nome de exibição"}
              />
              
              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="dense"
                multiline
                rows={2}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Role Ativa"
                sx={{ mt: 2 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Permissões
            </Typography>
            
            {Object.entries(permissionGroups).map(([module, modulePermissions]) => (
              <Box key={module} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {module.charAt(0).toUpperCase() + module.slice(1)}
                </Typography>
                <List dense>
                  {modulePermissions.map((permission) => (
                    <ListItem key={permission.id} disablePadding>
                      <ListItemIcon>
                        <Checkbox
                          checked={formData.permission_ids.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={permission.display_name}
                        secondary={permission.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button 
              onClick={() => runSubmit(handleSaveRole)}
              variant="contained"
              disabled={submitting || !formData.display_name || !formData.name}
            >
              {selectedRole ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default RoleManagement;